import { useState } from "react";
import { useListEcolePaiements, useGetEcoleDashboard } from "@/lib/backoffice-hooks";
import { formatDateShort } from "@/lib/format";
import EcoleShell from "@/components/layout/EcoleShell";
import { Card } from "@/components/ui/card";
import { Loader2, Download, FileSpreadsheet, Eye } from "lucide-react";
import { downloadRecuPdf, downloadCsv } from "@/lib/downloadRecu";
import RecuPreviewDialog from "@/components/RecuPreviewDialog";
import ReconciliationUnifiee from "@/components/ReconciliationUnifiee";

function fcfa(n: number) {
  return new Intl.NumberFormat("fr-FR").format(n) + " FCFA";
}
const STATUSES = ["", "SUCCESS", "PROCESSING", "PENDING", "FAILED", "CANCELLED"] as const;
const STATUS_LABEL: Record<string, string> = {
  SUCCESS: "Encaissé", PROCESSING: "En cours", PENDING: "En attente",
  FAILED: "Échoué", CANCELLED: "Annulé", REFUNDED: "Remboursé",
};

export default function PaiementsPage() {
  const [status, setStatus] = useState<string>("");
  const [apercu, setApercu] = useState<{ id: string; reference: string } | null>(null);
  const { data, isLoading } = useListEcolePaiements(status ? { status } : undefined);
  // Scope économe : l'établissement est résolu côté backend (JWT) et exposé par
  // le dashboard école → on réutilise son `ecoleId` pour la vue unifiée.
  const { data: dashboard } = useGetEcoleDashboard();
  return (
    <EcoleShell>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-navy">Paiements</h1>
          <p className="text-sm text-slate-600">{data?.length ?? 0} paiement(s) {status ? `· statut ${STATUS_LABEL[status]}` : ""}</p>
        </div>
        <div className="flex flex-wrap gap-1 items-center">
          {STATUSES.map((s) => (
            <button
              key={s || "ALL"}
              onClick={() => setStatus(s)}
              className={`text-xs px-3 py-1.5 rounded border ${status === s ? "bg-navy text-white border-navy" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
              data-testid={`filter-${s || "all"}`}
            >
              {s ? STATUS_LABEL[s] : "Tous"}
            </button>
          ))}
          <button
            onClick={() => {
              const rows = (data ?? []).map((p) => ({
                Reference: p.reference,
                Eleve: p.eleveNom,
                Motif: p.motifLibelle,
                Montant_FCFA: p.montantXof,
                Mode: p.methode,
                Driver: p.driver,
                Statut: STATUS_LABEL[p.status] ?? p.status,
                Date_creation: p.createdAt ? new Date(p.createdAt).toLocaleString("fr-FR") : "",
                Date_paiement: p.paidAt ? new Date(p.paidAt).toLocaleString("fr-FR") : "",
              }));
              downloadCsv(`paiements-${new Date().toISOString().slice(0, 10)}.csv`, rows);
            }}
            className="text-xs px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50 inline-flex items-center gap-1.5 ml-2"
            data-testid="button-export-csv"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      <Card className="border border-slate-200 shadow-none overflow-hidden">
        {isLoading && <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-navy" /></div>}
        {!isLoading && data && (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Référence</th>
                <th className="text-left">Élève</th>
                <th className="text-left">Motif</th>
                <th className="text-right">Montant</th>
                <th className="text-left pl-3">Mode</th>
                <th className="text-left">Driver</th>
                <th className="text-left">Statut</th>
                <th className="text-left">Date</th>
                <th className="text-center pr-4">Reçu</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-500">Aucun paiement.</td></tr>}
              {data.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`row-paiement-${p.reference}`}>
                  <td className="px-4 py-2.5 font-mono text-xs text-navy" style={{ fontFamily: "JetBrains Mono, monospace" }}>{p.reference || "·"}</td>
                  <td>{p.eleveNom}</td>
                  <td className="text-slate-600">{p.motifLibelle}</td>
                  <td className="text-right font-mono" style={{ fontFamily: "JetBrains Mono, monospace" }}>{fcfa(p.montantXof)}</td>
                  <td className="pl-3 text-xs text-slate-600">{String(p.methode).replace(/_/g, " ")}</td>
                  <td className="text-xs text-slate-500">{p.driver}</td>
                  <td>
                    <span className={`text-xs px-2 py-0.5 rounded ${p.status === "SUCCESS" ? "bg-emerald-100 text-emerald-700" : p.status === "FAILED" || p.status === "CANCELLED" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                      {STATUS_LABEL[p.status] ?? p.status}
                    </span>
                  </td>
                  <td className="text-xs text-slate-500">{formatDateShort(p.paidAt ?? p.createdAt)}</td>
                  <td className="text-center pr-4">
                    {p.status === "SUCCESS" && (
                      <div className="inline-flex items-center gap-0.5">
                        <button onClick={() => setApercu({ id: p.id, reference: p.reference })} title="Aperçu du reçu" className="text-slate-500 hover:text-navy p-1" data-testid={`button-apercu-${p.reference}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => downloadRecuPdf(p.id, p.reference)} title="Télécharger le reçu PDF" className="text-slate-500 hover:text-navy p-1" data-testid={`button-pdf-${p.reference}`}>
                          <Download className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <ReconciliationUnifiee etablissementId={dashboard?.ecoleId} />

      <RecuPreviewDialog
        open={apercu !== null}
        onOpenChange={(o) => !o && setApercu(null)}
        source={{ kind: "auth", paiementId: apercu?.id ?? "" }}
        reference={apercu?.reference}
      />
    </EcoleShell>
  );
}
